import { useState } from 'react';
import axios from 'axios';
import { Mail, Phone, MapPin, CheckCircle, AlertCircle, Factory } from 'lucide-react';
import SEO from '@/components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    productType: '',
    message: '',
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });

    try {
      await axios.post(`${API}/contact-quote`, formData);
      setStatus({
        type: 'success',
        message: 'Thank you for your inquiry! We will get back to you within 24 hours.',
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        productType: '',
        message: '',
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Something went wrong. Please try again or contact us directly.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        path="/contact"
        title="Contact Arkotherm Furnaces — Request a Quote | Amritsar & Ludhiana"
        description="Get in touch with Arkotherm Furnaces for industrial furnace enquiries, spare parts and custom heat-treatment solutions. Call +91 62804 52690 or email rkkohli.sons@gmail.com."
      />
      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-block px-4 py-2 bg-orange-500/20 border border-orange-500 rounded-full text-sm font-semibold mb-4">
              Contact Us · ISO 9001 Certified Company
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
              Let's Build Something <span className="gradient-text">Great Together</span>
            </h1>
            <p className="text-xl text-gray-300">
              Ready to discuss your heat treatment needs? Fill out the form below and our team will
              respond within 24 hours.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <Phone className="text-orange-500 mb-4" size={32} />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Phone</h3>
                <p className="text-gray-700">
                  <a href="tel:+916280452690" className="hover:text-orange-600 transition-colors">+91 62804 52690</a>
                </p>
                <p className="text-gray-700">
                  <a href="tel:+916239019768" className="hover:text-orange-600 transition-colors">+91 62390 19768</a>
                </p>
                <p className="text-gray-700">
                  <a href="tel:+919814851185" className="hover:text-orange-600 transition-colors">+91 98148 51185</a>
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg">
                <Mail className="text-orange-500 mb-4" size={32} />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Email</h3>
                <a
                  href="mailto:rkkohli.sons@gmail.com"
                  className="text-orange-600 hover:underline break-all"
                >
                  rkkohli.sons@gmail.com
                </a>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg">
                <Factory className="text-orange-500 mb-4" size={32} />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Factories</h3>
                <p className="text-gray-700 font-semibold">Amritsar &amp; Ludhiana</p>
                <p className="text-gray-500 text-sm mt-1">Manufacturing facilities in Punjab, India</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg">
                <MapPin className="text-orange-500 mb-4" size={32} />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Registered Address</h3>
                <address className="text-gray-700 not-italic">
                  173- Mehta Road<br />
                  Focal Point, Industrial Area<br />
                  Amritsar-143006,<br />
                  Punjab, India
                </address>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Request a Quote</h2>

                {status.message && (
                  <div
                    className={`mb-6 p-4 rounded-lg flex items-start gap-3 ${
                      status.type === 'success'
                        ? 'bg-green-50 border border-green-200'
                        : 'bg-red-50 border border-red-200'
                    }`}
                    data-testid="form-status-message"
                  >
                    {status.type === 'success' ? (
                      <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                    ) : (
                      <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                    )}
                    <p
                      className={`${
                        status.type === 'success' ? 'text-green-800' : 'text-red-800'
                      }`}
                    >
                      {status.message}
                    </p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition"
                        placeholder="John Doe"
                        data-testid="contact-name-input"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition"
                        placeholder="john@example.com"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition"
                        placeholder="+91 62804 52690"
                        data-testid="contact-phone-input"
                      />
                    </div>

                    <div>
                      <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition"
                        placeholder="Your Company"
                        data-testid="contact-company-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="productType" className="block text-sm font-semibold text-gray-700 mb-2">
                      Product Interest
                    </label>
                    <select
                      id="productType"
                      name="productType"
                      value={formData.productType}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition"
                      data-testid="contact-product-select"
                    >
                      <option value="">Select a product type...</option>
                      <option value="annealing">Annealing Furnace</option>
                      <option value="continuous">Continuous Mesh Belt Furnace</option>
                      <option value="retort">Retort</option>
                      <option value="carburizing">Gas Carburizing Furnace</option>
                      <option value="spares">Spare Parts</option>
                      <option value="custom">Custom Solution</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition resize-none"
                      placeholder="Tell us about your requirements — capacity, target throughput, steel grade and process..."
                      data-testid="contact-message-textarea"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full px-8 py-4 gradient-bg text-white font-bold rounded-full hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
